import { z } from 'zod';
export const locationModes = ['RETAILER_LOCATION', 'BROWSER_GEOLOCATION', 'IP_PROXY', 'HYBRID'];
export const fulfillmentModes = ['SHIPPING', 'PICKUP', 'DELIVERY'];
export const classifications = [
    'CONFIRMED_COMPARABLE', 'LOCATION_UNVERIFIED', 'PRODUCT_MISMATCH', 'UNAVAILABLE',
    'MEMBERSHIP_ONLY', 'BLOCKED', 'INCOMPLETE_TOTAL', 'FAILED', 'INCONCLUSIVE'
];
export const confidenceLevels = ['HIGH', 'MEDIUM', 'LOW', 'NONE'];
export const locationSchema = z.object({
    zip: z.string().regex(/^\d{5}(?:-\d{4})?$/, 'Use a 5-digit US ZIP code'),
    city: z.string().default(''),
    state: z.string().max(2).default(''),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    proxyLocationId: z.string().optional()
});
export const runRequestSchema = z.object({
    productUrl: z.string().url(),
    adapterId: z.enum(['test-retailer', 'generic-jsonld', 'amazon', 'live-retailer']),
    fulfillmentMode: z.enum(fulfillmentModes),
    locationMode: z.enum(locationModes),
    locations: z.array(locationSchema).min(1).max(100),
    repeatCount: z.number().int().min(1).max(3).default(1),
    controlZip: z.string().regex(/^\d{5}$/).optional(),
    concurrency: z.number().int().min(1).max(10).default(5)
});
export function comparableBasePrice(observation) {
    if (observation.classification !== 'CONFIRMED_COMPARABLE')
        return undefined;
    return observation.prices.salePrice ?? observation.prices.regularPrice;
}
export function median(values) {
    if (!values.length)
        return undefined;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}
export function rankObservations(observations) {
    const groups = new Map();
    for (const observation of observations) {
        const group = groups.get(observation.requestedZip) ?? [];
        group.push(observation);
        groups.set(observation.requestedZip, group);
    }
    const comparable = [...groups.entries()].map(([zip, group]) => {
        const valid = group.filter(item => item.classification === 'CONFIRMED_COMPARABLE');
        const bases = valid.map(comparableBasePrice).filter((value) => value !== undefined);
        const totals = valid.map(item => item.prices.displayedFinalTotal).filter((value) => value !== undefined);
        const basesDiffer = new Set(bases.map(value => value.toFixed(2))).size > 1;
        const totalsDiffer = new Set(totals.map(value => value.toFixed(2))).size > 1;
        return { zip, baseMedian: median(bases), totalMedian: median(totals), unstable: basesDiffer || totalsDiffer, observations: group };
    });
    return comparable.sort((a, b) => (a.baseMedian ?? Number.POSITIVE_INFINITY) - (b.baseMedian ?? Number.POSITIVE_INFINITY));
}
export function sameProduct(a, b) {
    const strongKeys = ['sku', 'upc', 'model'];
    const present = strongKeys.filter(key => a[key] && b[key]);
    if (present.length && present.some(key => normalize(a[key]) !== normalize(b[key])))
        return false;
    const variantKeys = ['variant', 'size', 'color', 'quantity'];
    if (variantKeys.some(key => a[key] && b[key] && normalize(a[key]) !== normalize(b[key])))
        return false;
    return present.length > 0 || (!!a.title && !!b.title && normalize(a.title) === normalize(b.title));
}
function normalize(value) { return (value ?? '').trim().toLowerCase(); }
export function redactSecrets(value) {
    return value.replace(/(https?:\/\/)([^\s:@/]+):([^\s@/]+)@/gi, '$1[redacted]:[redacted]@');
}
